import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock, CreditCard, User, MessageSquare } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCart, useAuth, API } from '../App';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, cartCount, sessionId } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    name: user?.name || '',
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    notes_to_seller: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (cartCount === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!formData.email || !formData.name) {
      toast.error('Please fill in your email and name');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin,
          customer_email: formData.email,
          customer_name: formData.name,
          cart_session_id: sessionId,
          shipping_address: {
            address: formData.address,
            city: formData.city,
            postcode: formData.postcode,
            country: formData.country
          },
          notes_to_seller: formData.notes_to_seller || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  if (cartCount === 0) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-[#2C2C2C] mb-4">Your Cart is Empty</h1>
          <Link to="/shop">
            <Button className="btn-primary">Shop Now</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="checkout-page">
      {/* Header */}
      <div className="bg-[#5D6D53] py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/cart')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Cart
          </button>
          <h1 className="font-serif text-3xl md:text-4xl text-white">Guest Checkout</h1>
          <p className="text-white/70 mt-2">No account required</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Guest Checkout Notice */}
        {!user && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#F5F1EB] rounded-xl p-4 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-[#C5A059]" />
              <p className="text-[#595959]">
                Have an account? <Link to="/login" className="text-[#C5A059] hover:underline font-medium">Sign in</Link> for faster checkout
              </p>
            </div>
          </motion.div>
        )}

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Checkout Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <form onSubmit={handleCheckout} className="space-y-8">
              {/* Contact Information */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
                <h2 className="font-serif text-xl text-[#2C2C2C] mb-6">Contact Information</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="input-glow mt-1"
                      placeholder="your@email.com"
                      data-testid="email-input"
                    />
                    <p className="text-xs text-[#8C8C8C] mt-1">We'll send your order confirmation here</p>
                  </div>
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="input-glow mt-1"
                      placeholder="John Smith"
                      data-testid="name-input"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
                <h2 className="font-serif text-xl text-[#2C2C2C] mb-6">Shipping Address</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="input-glow mt-1"
                      placeholder="123 High Street"
                      data-testid="address-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        name="city"
                        type="text"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="input-glow mt-1"
                        placeholder="Edinburgh"
                        data-testid="city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postcode">Postcode</Label>
                      <Input
                        id="postcode"
                        name="postcode"
                        type="text"
                        value={formData.postcode}
                        onChange={handleInputChange}
                        className="input-glow mt-1"
                        placeholder="EH1 1AA"
                        data-testid="postcode-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      name="country"
                      type="text"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="input-glow mt-1"
                      disabled
                    />
                  </div>
                </div>
              </div>

              {/* Notes to Seller */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
                <div className="flex items-center gap-3 mb-4">
                  <MessageSquare className="w-5 h-5 text-[#C5A059]" />
                  <h2 className="font-serif text-xl text-[#2C2C2C]">Notes to Seller</h2>
                </div>
                <Textarea
                  id="notes_to_seller"
                  name="notes_to_seller"
                  value={formData.notes_to_seller}
                  onChange={handleInputChange}
                  className="input-glow min-h-[100px]"
                  placeholder="Any special requests or instructions? (e.g., gift message, specific delivery instructions...)"
                  data-testid="notes-input"
                />
                <p className="text-xs text-[#8C8C8C] mt-2">Optional - These notes will be shared with Sarah</p>
              </div>

              {/* Payment Notice */}
              <div className="bg-[#F5F1EB] rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <CreditCard className="w-6 h-6 text-[#C5A059] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-[#2C2C2C] mb-1">Secure Payment</h3>
                    <p className="text-sm text-[#595959]">
                      You'll be redirected to Stripe's secure checkout to complete your payment. We accept all major credit and debit cards.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="pay-btn"
              >
                {loading ? (
                  <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Pay £{(cart.total + 4).toFixed(2)}
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100 sticky top-28">
              <h2 className="font-serif text-xl text-[#2C2C2C] mb-6">Order Summary</h2>

              {/* Items */}
              <div className="space-y-4 mb-6 max-h-[400px] overflow-y-auto">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex gap-4">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F5F1EB] flex-shrink-0">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#C5A059] to-[#E5C88A] opacity-30" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#2C2C2C] truncate">{item.name}</p>
                      <p className="text-sm text-[#8C8C8C]">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium text-[#2C2C2C]">£{item.subtotal?.toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-stone-200 pt-4 space-y-3">
                <div className="flex justify-between text-[#595959]">
                  <span>Subtotal</span>
                  <span>£{cart.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#595959]">
                  <span>Postage & Packing</span>
                  <span>£4.00</span>
                </div>
              </div>

              <div className="border-t border-stone-200 pt-4 mt-4">
                <div className="flex justify-between text-lg font-semibold">
                  <span className="font-serif">Total</span>
                  <span className="text-[#C5A059]" data-testid="checkout-total">
                    £{(cart.total + 4).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
